'use client';

import { useState } from 'react';
import { useIsHydrated } from '@/hooks/useIsHydrated';
import Icon from '@/components/ui/AppIcon';
import InsightCard from './InsightCard';
import FeaturedInsight from './FeaturedInsight';
import CategoryFilter from './CategoryFilter';
import NewsletterSignup from './NewsletterSignup';
import SearchBar from './SearchBar';
import Reveal from '@/components/common/Reveal';
import { insightCategories, industryPros, industryCons, type Insight } from '../insightsData';

interface Props {
  insights: Insight[];
}

const SustainabilityInsightsInteractive: React.FC<Props> = ({ insights }) => {
  const isHydrated = useIsHydrated();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const filteredInsights = insights.filter((insight) => {
    const matchesCategory = activeCategory === 'All' || insight.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      insight.title.toLowerCase().includes(q) || insight.excerpt.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  const featuredInsight = filteredInsights.find((i) => i.isFeatured);
  const regularInsights = filteredInsights.filter((i) => !i.isFeatured);

  if (!isHydrated) return <div className="min-h-screen bg-background pt-28" aria-hidden="true" />;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-16 lg:pb-24">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Reveal as="header" className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-leaf/15 border border-leaf/30 font-cta text-sm font-medium uppercase tracking-wider text-foreground">
            <Icon name="LightBulbIcon" size={16} className="text-leaf-dark" aria-hidden="true" />
            Insights &amp; Impact
          </span>
          <h1 className="font-headline text-2xl md:text-3xl lg:text-3xl font-bold text-foreground leading-[1.05] tracking-tight mb-4">
            The story behind sustainable foodware
          </h1>
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            An honest look at the biodegradable industry - its real strengths and trade-offs - and
            the women-led team turning agricultural waste into premium, planet-friendly tableware.
          </p>
        </Reveal>

        {/* ── Pros & Cons of the biodegradable industry ─────────────────── */}
        <section aria-labelledby="proscons-title" className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2
              id="proscons-title"
              className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-2"
            >
              The biodegradable industry, honestly
            </h2>
            <p className="font-body text-lg text-muted-foreground leading-relaxed">
              Compostable foodware is a genuine step forward - but it is a trade-off, not a miracle.
              Here is where it wins, and where buyers should stay sharp.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Pros */}
            <div className="rounded-2xl border border-success/25 bg-success/5 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-success/15">
                  <Icon name="CheckCircleIcon" size={22} className="text-success" />
                </span>
                <h3 className="font-cta text-lg font-semibold text-foreground">
                  What genuinely works
                </h3>
              </div>
              <ul className="space-y-4">
                {industryPros.map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      className="text-success shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="font-cta text-sm font-semibold text-foreground">{item.title}</p>
                      <p className="font-body text-sm text-muted-foreground leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cons */}
            <div className="rounded-2xl border border-accent/25 bg-accent/5 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/15">
                  <Icon name="InformationCircleIcon" size={22} className="text-accent" />
                </span>
                <h3 className="font-cta text-lg font-semibold text-foreground">
                  Where to stay sharp
                </h3>
              </div>
              <ul className="space-y-4">
                {industryCons.map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <Icon name="XCircleIcon" size={18} className="text-accent shrink-0 mt-0.5" />
                    <div>
                      <p className="font-cta text-sm font-semibold text-foreground">{item.title}</p>
                      <p className="font-body text-sm text-muted-foreground leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="font-body text-sm text-muted-foreground mt-5 max-w-3xl">
            Our take: biodegradable foodware is the right direction for most food-service buyers -
            provided you buy certified product, plan for composting, and match the material to the
            job.
          </p>
        </section>

        {/* ── Editorial content pillars ─────────────────────────────────── */}
        <section aria-labelledby="pillars-title" className="mb-16">
          <h2 id="pillars-title" className="sr-only">
            What you&apos;ll find here
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ChartBarIcon',
                label: 'Industry analysis',
                body: 'Honest, evidence-based takes on the biodegradable and compostable foodware market  - including the trade-offs buyers need to know.',
                accent: 'bg-primary/8 border-primary/15',
                iconBg: 'bg-primary/10',
                iconColor: 'text-primary',
              },
              {
                icon: 'BeakerIcon',
                label: 'Sustainability science',
                body: 'How sugarcane bagasse composts, what EN 13432 certification actually verifies, and which environmental claims a supplier can legitimately make.',
                accent: 'bg-leaf/8 border-leaf/15',
                iconBg: 'bg-leaf/10',
                iconColor: 'text-leaf-dark',
              },
              {
                icon: 'BriefcaseIcon',
                label: 'Buyer guides',
                body: 'Practical guidance for importers and distributors  - from MOQ planning and certification requirements to compliance documentation.',
                accent: 'bg-accent/8 border-accent/15',
                iconBg: 'bg-accent/10',
                iconColor: 'text-accent',
              },
            ].map((pillar, i) => (
              <Reveal
                as="div"
                key={pillar.label}
                delay={i * 70}
                className={`h-full rounded-2xl border p-6 lg:p-8 ${pillar.accent}`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl mb-4 ${pillar.iconBg}`}
                >
                  <Icon
                    name={pillar.icon}
                    size={20}
                    className={pillar.iconColor}
                    aria-hidden="true"
                  />
                </span>
                <p className="font-cta text-sm font-semibold text-foreground mb-2">
                  {pillar.label}
                </p>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">
                  {pillar.body}
                </p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ── Articles ──────────────────────────────────────────────────── */}
        <section aria-labelledby="articles-title">
          <div className="mb-6">
            <h2
              id="articles-title"
              className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-4"
            >
              Latest articles
            </h2>
            <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
              <div className="w-full lg:w-auto">
                <SearchBar onSearch={setSearchQuery} />
              </div>
              <CategoryFilter
                categories={insightCategories as string[]}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </div>
          </div>

          {featuredInsight && (
            <div className="mb-10">
              <FeaturedInsight insight={featuredInsight} />
            </div>
          )}

          {regularInsights.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {regularInsights.map((insight, i) => (
                <Reveal as="div" key={insight.id} delay={(i % 3) * 70}>
                  <InsightCard insight={insight} />
                </Reveal>
              ))}
            </div>
          ) : (
            !featuredInsight && (
              <div className="text-center py-10">
                <p className="font-body text-lg text-muted-foreground">
                  No articles found matching your criteria.
                </p>
              </div>
            )
          )}
        </section>

        {/* ── Newsletter ────────────────────────────────────────────────── */}
        <div className="mt-12">
          <NewsletterSignup />
        </div>
      </div>
    </div>
  );
};

export default SustainabilityInsightsInteractive;
